/**
 * What a signed-out viewer was doing when the watch screen asked them to sign in. Kept in the browser across the
 * Google, Strava or emailed-link round trip (the API's magic link carries only a return path), then completed once
 * on the same event's screen.
 */
export type WatchIntent =
  | { kind: 'chat'; eventId: string; stageId: string; draft: string }
  | { kind: 'go_live'; eventId: string; stageId: string }
  /** Remind me on the pre-show (docs/features/pre-show-page.md): registers once signed in. */
  | { kind: 'remind'; eventId: string; stageId: string }

const KEY = 'sb_watch_intent'
/** This tab's own record (sessionStorage) of the intent it left with for Google or Strava: the saved intent's time. */
const LEFT_KEY = 'sb_watch_intent_left'
const TTL_MS = 10 * 60 * 1000

/** A saved intent, and whether this tab saved it on its way to Google or Strava (and so is the tab coming back). */
export interface SavedIntent {
  intent: WatchIntent
  leftFromHere: boolean
}

/**
 * Saves what the viewer was doing. `leaving` marks this tab as the one leaving for a provider, which comes back here
 * signed in, or signed out after Back (`peekIntent`); an emailed link signs in another tab, so it doesn't.
 */
export function saveIntent(intent: WatchIntent, { leaving = false }: { leaving?: boolean } = {}) {
  const at = Date.now()
  try {
    localStorage.setItem(KEY, JSON.stringify({ intent, at }))
  } catch {
    // Private mode: the intent is lost, the sign-in still works.
    return
  }
  try {
    if (leaving) sessionStorage.setItem(LEFT_KEY, String(at))
    else sessionStorage.removeItem(LEFT_KEY)
  } catch {
    // Without sessionStorage no tab counts as the one that left.
  }
}

/**
 * The saved intent for this event, without taking it. An intent older than 10 minutes, or one that isn't an intent, is
 * dropped; one for another event stays for that event's screen (null here).
 */
export function peekIntent(eventId: string): SavedIntent | null {
  try {
    const saved = readSaved()
    if (!saved || saved.intent.eventId !== eventId) return null
    return { intent: saved.intent, leftFromHere: leftAt() === saved.at }
  } catch {
    return null
  }
}

/** The saved intent for this event, once: taking it removes it. Dropped and kept as `peekIntent` says. */
export function takeIntent(eventId: string): WatchIntent | null {
  const saved = peekIntent(eventId)
  if (saved) clearIntent()
  return saved?.intent ?? null
}

/**
 * Forgets the saved intent, whatever its event. A sign-in that failed or was cancelled leaves nothing behind for the
 * next person who signs in on this browser.
 */
export function clearIntent() {
  try {
    localStorage.removeItem(KEY)
  } catch {
    // Private mode: nothing was saved.
  }
  try {
    sessionStorage.removeItem(LEFT_KEY)
  } catch {
    // Nothing to forget.
  }
}

/** The stored intent while it is current; an expired or unreadable one is removed. Throws when storage does. */
function readSaved(): { intent: WatchIntent; at: number } | null {
  const raw = localStorage.getItem(KEY)
  if (!raw) return null
  const saved = parse(raw)
  const age = saved ? Date.now() - saved.at : NaN
  // A negative age is a clock that moved back; without the check that intent would never expire.
  if (!saved || !(age >= 0 && age <= TTL_MS)) {
    localStorage.removeItem(KEY)
    return null
  }
  return saved
}

function leftAt(): number | null {
  try {
    const value = sessionStorage.getItem(LEFT_KEY)
    return value === null ? null : Number(value)
  } catch {
    return null
  }
}

function parse(raw: string): { intent: WatchIntent; at: number } | null {
  try {
    const value = JSON.parse(raw) as { intent?: unknown; at?: unknown } | null
    if (typeof value?.at !== 'number' || !isWatchIntent(value.intent)) return null
    return { intent: value.intent, at: value.at }
  } catch {
    return null
  }
}

function isWatchIntent(value: unknown): value is WatchIntent {
  const intent = value as Partial<Record<keyof WatchIntent | 'draft', unknown>> | null
  if (typeof intent?.eventId !== 'string' || typeof intent.stageId !== 'string') return false
  return intent.kind === 'go_live' || intent.kind === 'remind' || (intent.kind === 'chat' && typeof intent.draft === 'string')
}
